import { useState } from "react";
import { FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useReport, useExportPdf, useExportExcel } from "@/hooks/useReports";
import { useActiveEmployees } from "@/hooks/useEmployees";
import { CustomerCombobox } from "@/features/complaints/CustomerCombobox";
import { ReportViewer } from "./ReportViewer";
import { ReportType, ReportParams } from "@/api/report.api";

const REPORT_TYPES: { value: ReportType; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "status", label: "Complaint Status" },
  { value: "employee", label: "Employee" },
  { value: "customer", label: "Customer" },
];

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}
function thisMonth(): string {
  return new Date().toISOString().slice(0, 7);
}

export function ReportsPage() {
  const [type, setType] = useState<ReportType>("daily");
  const [date, setDate] = useState(todayISO());
  const [month, setMonth] = useState(thisMonth());
  const [employeeId, setEmployeeId] = useState("");
  const [customerId, setCustomerId] = useState("");

  const { toast } = useToast();
  const { data: employees } = useActiveEmployees();
  const exportPdf = useExportPdf();
  const exportExcel = useExportExcel();

  const params: ReportParams = {
    type,
    date: type === "daily" || type === "weekly" ? date : undefined,
    month: type === "monthly" ? month : undefined,
    employeeId: type === "employee" ? employeeId : undefined,
    customerId: type === "customer" ? customerId : undefined,
  };

  const { data: report, isLoading } = useReport(params);

  const handleExport = async (format: "pdf" | "excel") => {
    try {
      if (format === "pdf") await exportPdf.mutateAsync(params);
      else await exportExcel.mutateAsync(params);
    } catch {
      toast({ title: `Could not export ${format.toUpperCase()}`, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl font-semibold">Reports</h1>
        <p className="text-sm text-muted-foreground">Generate and export operational reports.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-border bg-card p-4">
        <div className="space-y-1.5">
          <Label>Report type</Label>
          <select
            className="flex h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm"
            value={type}
            onChange={(e) => setType(e.target.value as ReportType)}
          >
            {REPORT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {(type === "daily" || type === "weekly") && (
          <div className="space-y-1.5">
            <Label>{type === "daily" ? "Date" : "Any date in the week"}</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        )}

        {type === "monthly" && (
          <div className="space-y-1.5">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
        )}

        {type === "employee" && (
          <div className="min-w-[200px] space-y-1.5">
            <Label>Employee</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            >
              <option value="">Select employee</option>
              {employees?.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.fullName}
                </option>
              ))}
            </select>
          </div>
        )}

        {type === "customer" && (
          <div className="min-w-[260px] space-y-1.5">
            <Label>Customer</Label>
            <CustomerCombobox value={customerId} onChange={setCustomerId} />
          </div>
        )}

        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={!report || exportExcel.isPending}
          >
            {exportExcel.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="h-4 w-4" />
            )}
            Export Excel
          </Button>
          <Button onClick={() => handleExport("pdf")} disabled={!report || exportPdf.isPending}>
            {exportPdf.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Export PDF
          </Button>
        </div>
      </div>

      <ReportViewer report={report} isLoading={isLoading} />
    </div>
  );
}
